const MAX_BYTES = 5 * 1024 * 1024;
const MAX_WIDTH = 1920;
const MAX_HEIGHT = 1440;

/** Recipe-only preparation; never falls back to an oversized original. */
export async function prepareRecipeImage(file: File): Promise<File> {
  if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) throw new Error('Select a JPEG, PNG, or WebP image.');
  const image = await loadImage(URL.createObjectURL(file));
  try {
    const preservePng = file.type === 'image/png';
    let scale = Math.min(1, MAX_WIDTH / image.width, MAX_HEIGHT / image.height);
    let quality = 0.85;
    for (let attempt = 0; attempt < 6; attempt += 1) {
      const blob = await draw(image, Math.max(1, Math.round(image.width * scale)), Math.max(1, Math.round(image.height * scale)), preservePng ? 'image/png' : 'image/jpeg', preservePng ? undefined : quality);
      if (blob.size <= MAX_BYTES) return new File([blob], `${file.name.replace(/\.[^.]+$/, '') || 'recipe-image'}.${preservePng ? 'png' : 'jpg'}`, { type: blob.type, lastModified: file.lastModified });
      if (!preservePng && quality > 0.55) quality = Math.max(0.55, quality - 0.1);
      else scale *= 0.75;
    }
    throw new Error('Image could not be optimized within the 5 MB upload limit.');
  } finally { URL.revokeObjectURL(image.src); }
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => { const image = new Image(); image.onload = () => resolve(image); image.onerror = () => reject(new Error('Image could not be decoded.')); image.src = src; });
}

function draw(image: HTMLImageElement, width: number, height: number, type: string, quality?: number): Promise<Blob> {
  const canvas = document.createElement('canvas'); canvas.width = width; canvas.height = height;
  const context = canvas.getContext('2d'); if (!context) return Promise.reject(new Error('Image processing is unavailable.'));
  context.drawImage(image, 0, 0, width, height);
  return new Promise((resolve, reject) => canvas.toBlob(blob => blob ? resolve(blob) : reject(new Error('Image processing failed.')), type, quality));
}
